import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { login } from '../store/authSlice';
import { AppDispatch, RootState } from '../store';
import { useTranslation } from 'react-i18next';
import { useToast } from '../components/ToastProvider';
import logo from '../assets/images/logo_with_text_lg.png';

// Critical LCP asset loaded from public folder for optimal performance
const clouds = '/assets/images/clouds.webp';

const Login: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const { loading, error } = useSelector((state: RootState) => state.auth);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Calculate daily changing hue value for cloud background
  const today = new Date();
  const startOfYear = new Date(today.getFullYear(), 0, 0);
  const diff = today.getTime() - startOfYear.getTime();
  const oneDay = 1000 * 60 * 60 * 24;
  const dayOfYear = Math.floor(diff / oneDay);
  const hue = dayOfYear % 360;

  const backgroundStyle: React.CSSProperties = {
    backgroundColor: 'white',
    position: 'relative',
    overflow: 'hidden',
  };

  const cloudImageStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundImage: `url(${clouds})`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    filter: `hue-rotate(${hue}deg) saturate(70%) brightness(100%)`,
    opacity: 1.0,
    zIndex: 0,
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await dispatch(login({ email, password }));
    if (login.fulfilled.match(result)) {
      toast.show(t('auth.signInSuccess') as string, 'success');
      navigate('/dashboard');
    } else {
      toast.show(t('auth.invalidCredentials') as string, 'error');
    }
  };

  return (
    <div style={backgroundStyle} className="min-h-screen flex items-center justify-center px-4">
      <div style={cloudImageStyle}></div>
      {/* Global overlay above clouds for auth pages */}
      <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-[1]"></div>

      <div className="relative z-10 w-full max-w-md space-y-8">
        <div className="flex justify-center">
          <img src={logo} alt="HeartPort logo" className="h-14 md:h-20 w-auto" />
        </div>

        <div>
          <h2 className="text-center text-4xl font-bold text-[#49ABDB]">
            {t('auth.welcome')}
          </h2>
        </div>

        <div className="bg-[#CAD4DD] p-8 rounded-lg shadow-lg">
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <label className="block text-sm font-medium text-[#111D4E] mb-1">{t('auth.email')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-gray-800"
                autoComplete="email"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#111D4E] mb-1">{t('auth.password')}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-gray-800"
                autoComplete="current-password"
                required
              />
            </div>
            {error && <div className="text-sm text-red-600">{error}</div>}
            <button
              type="submit"
              className="w-full bg-[#47ABDB] text-white py-2 rounded-md font-semibold hover:bg-[#3d8fb8]"
              disabled={loading === 'pending'}
            >
              {t('auth.login')}
            </button>
          </form>
          <div className="text-sm mt-4 text-center">
            <button
              onClick={() => navigate('/reset-password')}
              className="text-[#47ABDB] hover:underline"
            >
              {t('auth.reset')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
